import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DashboardSidebar from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Car, Eye, Image } from "lucide-react";

interface Vehicle {
  id: string;
  brand: string;
  model: string;
  year: number;
  license_plate: string;
  display_name: string;
  image_url: string | null;
}

const ManageVehicles = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Vehicle[];
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("vehicles").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Voertuig verwijderd",
        description: "Het voertuig is succesvol verwijderd.",
      });

      // Refresh the page to update the list
      window.location.reload();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout bij verwijderen';
      toast({
        variant: "destructive",
        title: "Fout bij verwijderen",
        description: errorMessage,
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading vehicles</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <main className="flex-1 overflow-auto bg-white">
          <div className="p-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-[#B1A06D]">
                  Voertuigen Beheren
                </h1>
                <Button
                  onClick={() => navigate("/app/add-vehicle")}
                  className="bg-[#B1A06D] hover:bg-[#8A7F56]"
                >
                  <Car className="mr-2 h-4 w-4" />
                  Voertuig Toevoegen
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles?.map((vehicle) => (
                  <Card key={vehicle.id} className="flex flex-col h-full">
                    <div className="w-full h-48 relative">
                      {vehicle.image_url ? (
                        <img
                          src={vehicle.image_url}
                          alt={vehicle.display_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <Image className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    <div className="p-6 bg-[#F1F1F1] flex-1 flex flex-col">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-[#1A1F2C] mb-2">
                          {vehicle.display_name}
                        </h3>
                        <p className="text-lg text-[#1A1F2C]">
                          Bouwjaar: {vehicle.year}
                        </p>
                      </div>

                      <div className="flex justify-center mt-6 mb-6">
                        <div className="inline-flex h-[60px] items-center">
                          <div className="h-full w-[60px] bg-[#1C4B9C] flex items-center justify-center rounded-l-lg">
                            <span className="text-white font-bold">NL</span>
                          </div>
                          <div className="h-full px-6 bg-[#F7C748] flex items-center justify-center rounded-r-lg">
                            <span className="text-[#5D4E27] text-2xl font-bold">
                              {vehicle.license_plate}
                            </span>
                          </div>
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-[#B1A06D] hover:bg-[#8A7F56] text-white font-semibold"
                        onClick={() => navigate(`/app/vehicles/${vehicle.id}`)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Voertuig bekijken
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {vehicles?.length === 0 && (
                <div className="text-center py-12">
                  <Car className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    Geen voertuigen gevonden
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Begin met het toevoegen van een voertuig aan je wagenpark.
                  </p>
                  <Button
                    onClick={() => navigate("/app/add-vehicle")}
                    className="mt-4 bg-[#B1A06D] hover:bg-[#8A7F56]"
                  >
                    Voertuig Toevoegen
                  </Button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default ManageVehicles;
